import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useMediaQuery, useTheme, Box } from '@mui/material'

import { get } from 'utils/lodash'
import { getActiveDeactivatedLocations } from 'utils/location'
import { selectAddressName } from 'data/address/addressSelectors'
import { canSeeService } from 'data/permissions/permissionsSelectors'
import { CUSTOMER_DETAILS_TABS_ID } from 'containers/customer-details/settings'
import { isCSRedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { getCustomerDetails } from 'middleware/actions/customers'
import {
  selectIsLocationsPanelOpen,
  selectPanelSizes,
  setIsLocationsPanelOpen,
  setPanelSizes,
} from 'slices/customer/persistentCustomerDetailsServicesSlice'

import PanelButton from 'components/billing/review/disposal-tickets/common/PanelButton'
import ResizableSidePanelLayout from 'components/common/ResizableSidePanelLayout'
import { LAYOUT_HEIGHT } from '../layout/settings'
import { PANEL_MIN_SIZE } from './settings'
import LocationsPanel from './LocationsPanel'
import ServicesContentPanel from './ServicesContentPanel'

const { SERVICES_TAB_ID } = CUSTOMER_DETAILS_TABS_ID

const ServicesTab = ({ selectedTab = '', customerDetails = {}, onLocationChange }) => {
  const flags = useFlags()
  const dispatch = useDispatch()
  const isCSRedesignEnabled = isCSRedesignFeatureAvailable({ flags })
  const accountId = get(customerDetails, 'id')
  const [isServicesTabReady, setIsServicesTabReady] = useState(false)
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'))

  const seeService = useSelector(canSeeService, shallowEqual)
  const selectedLocationId = useSelector(state => get(state, 'CustomerDetails.selectedLocationId', ''))
  const showLocationsPanel = useSelector(selectIsLocationsPanelOpen)
  const panelSizes = useSelector(selectPanelSizes)

  const { activeLocations, deactivatedLocations } = getActiveDeactivatedLocations(customerDetails)

  const allLocations = [...activeLocations, ...deactivatedLocations]

  const currentLocation = allLocations.find(location => location.id === selectedLocationId)
  const currentLocationAddress = get(currentLocation, 'address', {})
  const currentLocationAddressName = selectAddressName(currentLocationAddress)

  const locationsPanelSize = useMemo(() => get(panelSizes, 'locationsPanel'), [panelSizes])
  const servicesPanelSize = useMemo(() => get(panelSizes, 'servicesPanel'), [panelSizes])

  // On mobile, always show the locations panel
  const shouldShowLocationsPanel = useMemo(() => {
    return isTabletOrMobile ? true : showLocationsPanel
  }, [isTabletOrMobile, showLocationsPanel])

  const refetchCustomerDetails = () => {
    dispatch(getCustomerDetails({ accountId }))
  }

  const handleLocationStatusChange = () => {
    if (selectedLocationId) {
      onLocationChange('')
    }
    refetchCustomerDetails()
  }

  const handleToggleLocationsPanel = () => {
    dispatch(setIsLocationsPanelOpen(!showLocationsPanel))
  }

  const handleLayoutChange = sizes => {
    if (showLocationsPanel && get(sizes, 'length', 0) === 2) {
      dispatch(setPanelSizes({ locationsPanel: sizes[0], servicesPanel: sizes[1] }))
    } else if (!showLocationsPanel && get(sizes, 'length', 0) === 1) {
      dispatch(setPanelSizes({ locationsPanel: panelSizes.locationsPanel, servicesPanel: 100 }))
    }
  }

  // Common props for components
  const locationsProps = {
    customerDetails,
    accountId,
    selectedLocationId,
    onLocationChange,
    onLocationStatusChange: handleLocationStatusChange,
    onSaveTags: refetchCustomerDetails,
    isMobile: isTabletOrMobile,
  }

  const servicesProps = {
    selectedLocationId,
    currentLocation,
    currentLocationAddress,
    currentLocationAddressName,
    seeService,
    isCSRedesignEnabled,
    accountId,
    activeLocations,
  }

  useEffect(() => {
    if (selectedTab === SERVICES_TAB_ID) {
      setIsServicesTabReady(true)
    }
  }, [selectedTab])

  return (
    isServicesTabReady && (
      <>
        {isTabletOrMobile ? (
          /* Mobile/Tablet: Simple vertical layout without resizable panels */
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden' }}>
            {shouldShowLocationsPanel && <LocationsPanel {...locationsProps} />}

            <Box
              sx={{
                flex: 1,
                backgroundColor: 'background.paper',
                overflow: 'auto',
                p: 1,
              }}
            >
              <ServicesContentPanel {...servicesProps} />
            </Box>
          </Box>
        ) : (
          /* Desktop: Resizable panels */
          <ResizableSidePanelLayout
            showSidePanel={showLocationsPanel}
            sidePanelSize={locationsPanelSize}
            mainPanelSize={showLocationsPanel ? servicesPanelSize : 100}
            minPanelSize={PANEL_MIN_SIZE}
            onLayoutChange={handleLayoutChange}
            sidePanel={<LocationsPanel {...locationsProps} />}
            mainPanel={
              <Box
                p={4}
                sx={{
                  backgroundColor: 'background.paper',
                  overflow: 'auto',
                  width: '100%',
                  height: LAYOUT_HEIGHT,
                }}
              >
                <ServicesContentPanel {...servicesProps} />
              </Box>
            }
            panelButton={<PanelButton showPanel={showLocationsPanel} onClick={handleToggleLocationsPanel} />}
            style={{ minWidth: 900, maxWidth: '100vw', height: '100%' }}
          />
        )}
      </>
    )
  )
}

ServicesTab.propTypes = {
  selectedTab: PropTypes.string,
  customerDetails: PropTypes.object,
  onLocationChange: PropTypes.func.isRequired,
}

export default ServicesTab
